import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipProps } from "recharts";
import type { PulsePoint } from "@/api/types";
import { dayShort, fmt } from "@/lib/format";
import { THEME } from "@/lib/theme";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export interface MessagesAreaProps {
  data: PulsePoint[];
}

/** Tooltip entry shape we consume. recharts' generic types are wide so
 * we narrow once here and use it below without leaking `any`. */
type ChartTooltipProps = TooltipProps<number, string>;

function GlowTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0];
  const raw = point?.value;
  const value = typeof raw === "number" ? raw : Number(raw ?? 0);
  const dateLabel = typeof label === "string" ? dayShort(label) : String(label ?? "");
  return (
    <div
      className="rounded-xl border border-white/10 bg-bg-1/80 px-3 py-2 text-xs shadow-lg backdrop-blur-md"
      style={{ minWidth: 110 }}
    >
      <div className="text-[10px] uppercase tracking-wider text-text-4">
        {dateLabel}
      </div>
      <div className="tabular mt-1 text-sm font-semibold text-text-1">
        {fmt(value)}{" "}
        <span className="text-[10px] font-normal uppercase tracking-wider text-text-4">
          mensajes
        </span>
      </div>
    </div>
  );
}

/** Area chart for "Mensajes por mes" in the Pulso tab. Glow brand gradient
 * with an animated draw-in and a custom dark tooltip. */
export function MessagesArea({ data }: MessagesAreaProps) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="grad-messages" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={THEME.brand.start} stopOpacity={0.3} />
            <stop offset="100%" stopColor={THEME.brand.end} stopOpacity={0} />
          </linearGradient>
          <filter id="glow-messages" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <CartesianGrid stroke={THEME.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          stroke={THEME.text.muted}
          tickFormatter={dayShort}
          tickLine={false}
          axisLine={false}
          fontSize={11}
        />
        <YAxis
          stroke={THEME.text.muted}
          tickFormatter={(v: number) => fmt(v)}
          tickLine={false}
          axisLine={false}
          fontSize={11}
          width={48}
        />
        <Tooltip
          cursor={{ stroke: "rgba(255,255,255,0.1)", strokeWidth: 1 }}
          content={<GlowTooltip />}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={THEME.brand.start}
          strokeWidth={2.25}
          fill="url(#grad-messages)"
          filter="url(#glow-messages)"
          activeDot={{
            r: 5,
            fill: THEME.brand.start,
            stroke: "#fff",
            strokeWidth: 1.5,
          }}
          isAnimationActive={!reducedMotion}
          animationDuration={900}
          animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
